import { useEffect, useRef, RefObject } from 'react';

// A CSS selector for all elements that can receive focus.
const FOCUSABLE_ELEMENTS_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A custom hook to trap focus within a specified container element (e.g., a modal).
 * When active, it prevents tabbing outside the container and returns focus
 * to the previously focused element when deactivated, ensuring accessibility.
 * @param {RefObject<HTMLElement>} containerRef - Ref to the container element that should trap focus.
 * @param {boolean} active - A boolean indicating if the focus trap should be currently active.
 */
export const useFocusTrap = (containerRef: RefObject<HTMLElement>, active: boolean) => {
  // A ref to store the element that was focused right before the trap was activated.
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Only run the effect if the trap is active and the container exists.
    if (!active || !containerRef.current) {
      return;
    }

    // Store the currently focused element so we can restore focus to it later.
    previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
    
    const container = containerRef.current;
    // Find all focusable elements within the container.
    const focusableElements = Array.from(
      container.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR)
    ) as HTMLElement[];

    // If there are no focusable elements, do nothing.
    if (focusableElements.length === 0) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Immediately move focus to the first focusable element in the container.
    firstElement.focus();

    /**
     * Handles the keydown event to trap the Tab key.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      // If Shift + Tab is pressed
      if (event.shiftKey) { 
        // If the current focus is on the first element, loop back to the last element.
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else { // If Tab is pressed
        // If the current focus is on the last element, loop forward to the first element.
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    // Cleanup function that runs when the effect is torn down (component unmounts or `active` becomes false).
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the element that was focused before the trap was activated.
      if (previouslyFocusedElementRef.current) {
        previouslyFocusedElementRef.current.focus();
      }
    };
  }, [active, containerRef]);
};
